import { RootSelector, getStore } from '../../reducers';
import { AppShape } from './model';
import { createSelector } from '@reduxjs/toolkit';
import { LoadAssetsFilter } from '../../api';
import { getSelectedIds as getProjectIds } from '../project/selectors';
import { getSelectedId as getCategoryId } from '../category/selectors';
import { getSelectedIds as getTagIds } from '../tag/selectors';
import { getSelectedIds as getExtensionIds } from '../extension/selectors';
import { getSearchApiParams } from '../search/selectors';
import { ALL_CATEGORIES_ID } from '../category/model';
import { ALL_EXTENSIONS_ID } from '../extension/model';
import { getCreative } from '../viewer/selectors';
import { MAGIC_STUDIO_KILL_SWITCH } from '../magicStudio/config';

export const getApp: RootSelector<AppShape> = createSelector(getStore, (state) => state.app);

export const getInitialized: RootSelector<boolean> = createSelector(
  getApp,
  (app) => app.initialized,
);

export const getUser = createSelector(getApp, (app) => app.user);

export const getPermissions = createSelector(getUser, (user) => {
  const result: Partial<Record<string, true>> = {};
  user?.permissions?.forEach((permission) => {
    result[permission] = true;
  });
  return result;
});

export const getAssetsFilter: RootSelector<LoadAssetsFilter> = createSelector(
  [getProjectIds, getCategoryId, getTagIds, getExtensionIds, getSearchApiParams, getCreative],
  (projectIds, categoryId, tagIds, extensionIds, searchApiParams, creative) => {
    const filter: LoadAssetsFilter = {
      project_id: projectIds.length ? projectIds.join() : undefined,
      tag: tagIds.join() || undefined,
      category: categoryId === ALL_CATEGORIES_ID ? undefined : categoryId,
      extension: extensionIds.includes(ALL_EXTENSIONS_ID)
        ? undefined
        : extensionIds.join() || undefined,
      ...searchApiParams,
      creative_id: creative ? String(creative.id) : undefined,
    };
    return filter;
  },
);

export const getSidebarExpanded = createSelector(getApp, (app) => app.sidebarExpanded);

export const getSidebarWidth = createSelector(getApp, (app) => app.sidebarWidth);

export const getSidebarDragging = createSelector(getApp, (app) => app.sidebarDragging);

export const getSettings = createSelector(getApp, (app) => app.settings);

// Effective Magic Studio visibility: the frontend kill-switch AND the backend's
// per-instance flag from GET /api/asset-lab/settings (MYUA-16447). Settings are
// loaded once on app start (see app/listeners.ts), so this is `false` until the
// response lands — the feature simply appears a beat after the rest of the UI,
// the same behaviour as the Google-bulk button.
export const getMagicStudioEnabled = createSelector(
  getSettings,
  (settings) => MAGIC_STUDIO_KILL_SWITCH && (settings?.magic_studio_enabled ?? false),
);

export const getSlowNetwork = createSelector(getApp, (app) => app.slowNetwork);
